<template>
  <div
    v-if="colors.length"
    class="color-swatches"
    data-testid="color-swatches"
  >
    <slot name="before-title" />

    <div
      class="flex-container align-justify"
    >
      <ColorInfo
        :id="name"
        :qty="numColors"
        :show-dropdown="showDropdown"
        :selected-color-name="selectedColorName"
        :color-label="colorLabel"
        :is-spacing-enabled="isSpacingEnabled"
        :is-spacing-2-enabled="isSpacing2Enabled"
      />
    </div>

    <div
      v-if="isMultiTiered && (isColorwayPricing || (isColorwayPricing2 && priceToColors[0]?.onSale))"
      class="align-section small margin-top-xxxs"
    >
      Pricing varies by color
      <button
        type="button"
        aria-label="Toggletip"
        class="help-button margin-left-xxxs"
        @click="toggletip.toggle"
      />
      <Toggletip
        id="colorway-pricing"
        ref="toggletip"
        position="bottom"
        :width="toggleTipWidth"
      >
        <p>{{ tooltipBody }}</p>
      </Toggletip>
    </div>

    <slot name="before-swatches" />
    <div v-if="colors.length > 1">
      <div
        v-if="showDropdown"
        class="margin-top-xs"
        data-testid="color-swatches-dropdown"
      >
        <Dropdown
          :id="`color-dropdown-${name}`"
          v-model="selectedColorIdString"
          :options="dropdownOptions"
          :disabled-option="true"
          label-size="none"
          @update:modelValue="open"
        />
      </div>
      <div
        v-for="([colorPane, expand]) in colorPanesZipped"
        :key="colorPane.index"
        data-testid="color-panel-container"
        class="overflow-gutters-row"
      >
        <div
          v-if="isMultiTiered && colorPane.price !== null"
          data-testid="color-panel-price"
          class="label overflow-gutters-item"
          :class="[{
            'on-sale-color': colorPane.price.onSale,
            'margin-top-xxs': isMultiTieredColorPricing,
            'margin-top-xs': !isMultiTieredColorPricing,
          } ]"
        >
          {{ colorPane.price.value }}
        </div>
        <template v-if="isExpandable">
          <ExpandableList
            v-model="expand.value"
            :row-height="isSpacingEnabled ? rowHeightSpacingEnabled : rowHeightSpacingDisabled"
            gap="0"
            expanded-text="See all colors"
            collapsed-text="See fewer colors"
            :active-item-index="selectedColorIndex"
            :is-current-pane-selected="selectedPaneIndex === colorPane.index"
            :max-visible-rows="2"
            :options="colorPane.colors"
            @expandable="onExpandable"
            @update:modelValue="tagColorExpandableListToggle"
          >
            <BaseColor
              v-model="selectedColorIdNum"
              :name="name"
              :colors="colorPane.colors"
              :sprite-urls="spriteUrls"
              :is-color-way="isMultiTiered"
              :is-spacing-enabled="isSpacingEnabled"
              :is-spacing-2-enabled="isSpacing2Enabled"
              :is-colorway-pricing2="isMultiTieredColorPricing2"
              class="overflow-gutters-item"
            >
              <template #swatch-icon="{ color }">
                <slot
                  name="swatch-icon"
                  :color="color"
                />
              </template>
            </BaseColor>
          </ExpandableList>
        </template>
        <template v-else>
          <BaseColor
            v-model="selectedColorIdNum"
            :name="name"
            :colors="colorPane.colors"
            :sprite-urls="spriteUrls"
            :is-color-way="isMultiTiered"
            :is-spacing-enabled="isSpacingEnabled"
            :is-spacing-2-enabled="isSpacing2Enabled"
            :is-colorway-pricing2="isMultiTieredColorPricing2"
            class="overflow-gutters-item"
          >
            <template #swatch-icon="{ color }">
              <slot
                name="swatch-icon"
                :color="color"
              />
            </template>
          </BaseColor>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import Dropdown from '@atomic-ui/dropdown';
import Toggletip from '@atomic-ui/toggletip';
import { prepareColorPanes } from '../utils/utils';
import ColorInfo from './ColorInfo';
import { ExpandableList } from '../../../components/common';
import BaseColor from './BaseColors';
import { useZip } from '../../../composables/useZip';
import { useToggle } from '../../../composables/useToggle';
import { tagColorExpandableListToggle } from '../../../analytics/color';

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  modelValue: {
    /** @type import('vue').PropType<Number | null> */
    type: Number,
    default: null,
  },
  colors: {
    /** @type import('vue').PropType<BaseColor[]> */
    type: Array,
    default: () => [],
  },
  spriteUrls: {
    /** @type import('vue').PropType<String[]> */
    type: Array,
    default: () => [],
  },
  showDropdown: {
    type: Boolean,
    default: false,
  },
  priceToColors: {
    /** @type import('vue').PropType<PriceToColorItem[]> */
    type: Array,
    default: () => [],
  },
  priceToColorsExp: {
    /** @type import('vue').PropType<PriceToColorExpItem[]> */
    type: Array,
    default: () => [],
  },
  isExpandable: {
    type: Boolean,
    default: true,
  },
  colorLabel: {
    type: String,
    default: 'Color',
  },
  isColorwayPricing: {
    type: Boolean,
    default: false,
  },
  isColorwayPricing2: {
    type: Boolean,
    default: false,
  },
  isSpacingEnabled: {
    type: Boolean,
    default: false,
  },
  isSpacing2Enabled: {
    type: Boolean,
    default: false,
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(['update:modelValue']);

const isExpandableList = ref(false);
const toggletip = ref(null);

const onExpandable = (isExpandable) => {
  if (isExpandableList.value === isExpandable) {
    return;
  }
  isExpandableList.value = isExpandable;
};

const isMultiTiered = computed(() => props.priceToColors.length > 1);

const isMultiTieredColorPricing = computed(() => isMultiTiered.value && (props.isColorwayPricing || props.isColorwayPricing2));
const isMultiTieredColorPricing2 = computed(() => isMultiTiered.value && props.isColorwayPricing2);
const toggleTipWidth = props.isMobile ? '292' : '335';
const tooltipBody = `Select a color to see the exact price from ${props.priceToColorsExp?.[0]?.priceRange}.`;

const rowHeightSpacingEnabled = (isMultiTiered.value && props.isSpacingEnabled) ? '47' : '55';
const rowHeightSpacingDisabled = (isMultiTiered.value && !props.isSpacingEnabled) ? '51' : '59';

const dropdownOptions = computed(() => props.colors.map((color) => ({
  value: color.id.toString(),
  displayValue: color.available ? color.name : `${color.name} is unavailable`,
})).sort((a, b) => a.displayValue.toLowerCase().localeCompare(b.displayValue.toLowerCase())));

const selectedColorIdNum = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emits('update:modelValue', newValue);
  },
});

const selectedColorIdString = computed({
  get() {
    return props.modelValue?.toString() || '';
  },
  set(newValue) {
    emits('update:modelValue', newValue);
  },
});

const selectedColorName = computed(() => props.colors.find((color) => color.id.toString() === selectedColorIdString.value)?.name);
const priceToColors = computed(() => (isMultiTieredColorPricing.value ? props.priceToColorsExp : props.priceToColors));
const colorPanes = computed(() => prepareColorPanes(priceToColors.value, props.colors, isMultiTieredColorPricing.value));
const numColors = computed(() => colorPanes.value.map((color) => color.colors.length).reduce((a, b) => a + b, 0));

const colorPanesZipped = computed(() => useZip(
  colorPanes.value,
  useToggle,
  { reactive: true, initializer: null },
).value);

const selectedColorIndex = computed(() => props.colors.findIndex((color) => color.id.toString() === selectedColorIdString.value));
const selectedPaneIndex = computed(() => (selectedColorIndex.value >= 0
  ? colorPanes.value.findIndex((colorPane) => colorPane.colors.some(((color) => color.id === props.colors[selectedColorIndex.value].id)))
  : 0));

/**
 * @param {string} colorIdString
 */
const open = (colorIdString) => {
  if (!isExpandableList.value) {
    return;
  }
  const colorPaneIndex = colorPanesZipped.value
    .findIndex(([colorPane]) => colorPane.colors.some((item) => colorIdString === item.id.toString()));

  const [, toggle] = colorPanesZipped.value[colorPaneIndex];
  toggle.open();
};
</script>

<style lang="scss" src="../../../scss/utils/_overflow-gutters.scss" />

<style lang="scss" scoped>
@import '@csgIcons/new/_tool-tip-dark-small.scss';

.on-sale-color {
  color: $sale-color;
}

// 4px of margin-left
.margin-left-xxxs {
  margin-left: calc(map-get($grid-margin-gutters, 'small') / 4);
}

// 4px of margin-top
.margin-top-xxxs {
  margin-top: calc(map-get($grid-margin-gutters, 'small') / 4);
}

.help-button {
  cursor: pointer;
  @extend .tool-tip-dark-small;
}

.align-section {
  display: flex;
  align-items: center;
  color: $gray-1-color;
}
</style>
